import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { Check, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const Registration = () => {
  const [formData, setFormData] = useState({
    parentName: '',
    phone: '',
    childAge: '',
    childGrade: '',
    annualEducationSpend: '',
    currentLearningChallenges: '',
    aiExposureLevel: '',
    learningGoals: '',
    preferredLearningStyle: '',
    deviceAccess: '',
    timeAvailability: '',
    motivationLevel: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [email, setEmail] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const userEmail = sessionStorage.getItem('userEmail');
    const userPaymentId = sessionStorage.getItem('paymentId');
    
    if (!userEmail || !userPaymentId) {
      navigate('/');
      return;
    }
    
    setEmail(userEmail);
    setPaymentId(userPaymentId);
  }, [navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await (supabase as any)
        .from('webinar_registrations')
        .insert({
          payment_id: paymentId,
          parent_name: formData.parentName,
          email: email,
          phone: formData.phone,
          child_age: parseInt(formData.childAge),
          child_grade: formData.childGrade,
          annual_education_spend: formData.annualEducationSpend,
          current_learning_challenges: formData.currentLearningChallenges,
          ai_exposure_level: formData.aiExposureLevel,
          learning_goals: formData.learningGoals,
          preferred_learning_style: formData.preferredLearningStyle,
          device_access: formData.deviceAccess,
          time_availability: formData.timeAvailability,
          motivation_level: formData.motivationLevel
        });

      if (error) throw error;

      // Clear session storage
      sessionStorage.removeItem('userEmail');
      sessionStorage.removeItem('paymentId');
      
      // Store registration data for welcome page
      sessionStorage.setItem('userData', JSON.stringify({
        firstName: formData.parentName,
        email: email
      }));

      toast({
        title: "Registration Complete!",
        description: "Welcome to AI Kids Academy. Check your email for webinar details.",
        variant: "default"
      });

      navigate('/welcome');

    } catch (error) {
      console.error('Registration error:', error);
      toast({
        title: "Error",
        description: "Failed to complete registration. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Home
          </button>

          <div className="bg-white p-8 rounded-xl shadow-lg">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Check className="h-4 w-4" />
                PAYMENT CONFIRMED
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Registration</h1>
              <p className="text-gray-600">Help us personalize your AI Kids Academy experience</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="parentName"
                    value={formData.parentName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="Parent/Guardian name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Child's Age *
                  </label>
                  <select
                    name="childAge"
                    value={formData.childAge}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select age</option>
                    {Array.from({ length: 13 }, (_, i) => i + 5).map(age => (
                      <option key={age} value={age}>{age} years</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Child's Grade
                  </label>
                  <select
                    name="childGrade"
                    value={formData.childGrade}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select grade</option>
                    <option value="Kindergarten">Kindergarten</option>
                    <option value="1st Grade">1st Grade</option>
                    <option value="2nd Grade">2nd Grade</option>
                    <option value="3rd Grade">3rd Grade</option>
                    <option value="4th Grade">4th Grade</option>
                    <option value="5th Grade">5th Grade</option>
                    <option value="6th Grade">6th Grade</option>
                    <option value="7th Grade">7th Grade</option>
                    <option value="8th Grade">8th Grade</option>
                    <option value="9th Grade">9th Grade</option>
                    <option value="10th Grade">10th Grade</option>
                    <option value="11th Grade">11th Grade</option>
                    <option value="12th Grade">12th Grade</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Annual Education Spend *
                </label>
                <select
                  name="annualEducationSpend"
                  value={formData.annualEducationSpend}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="">Select range</option>
                  <option value="Below ₹50,000">Below ₹50,000</option>
                  <option value="₹50,000 - ₹1,00,000">₹50,000 - ₹1,00,000</option>
                  <option value="₹1,00,000 - ₹2,00,000">₹1,00,000 - ₹2,00,000</option>
                  <option value="₹2,00,000 - ₹5,00,000">₹2,00,000 - ₹5,00,000</option>
                  <option value="Above ₹5,00,000">Above ₹5,00,000</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Learning Challenges
                </label>
                <textarea
                  name="currentLearningChallenges"
                  value={formData.currentLearningChallenges}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="What challenges does your child face in learning?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI Exposure Level *
                </label>
                <select
                  name="aiExposureLevel"
                  value={formData.aiExposureLevel}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                >
                  <option value="">Select exposure level</option>
                  <option value="No exposure">No exposure to AI</option>
                  <option value="Basic awareness">Basic awareness</option>
                  <option value="Some experience">Some hands-on experience</option>
                  <option value="Advanced user">Advanced user</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Learning Goals
                </label>
                <textarea
                  name="learningGoals"
                  value={formData.learningGoals}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder="What do you hope your child achieves through AI learning?"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Learning Style *
                  </label>
                  <select
                    name="preferredLearningStyle"
                    value={formData.preferredLearningStyle}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select style</option>
                    <option value="Visual">Visual (images, diagrams)</option>
                    <option value="Auditory">Auditory (listening, discussion)</option>
                    <option value="Kinesthetic">Kinesthetic (hands-on activities)</option>
                    <option value="Mixed">Mixed approach</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Device Access *
                  </label>
                  <select
                    name="deviceAccess"
                    value={formData.deviceAccess}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select device</option>
                    <option value="Laptop/Desktop">Laptop/Desktop</option>
                    <option value="Tablet">Tablet</option>
                    <option value="Smartphone">Smartphone only</option>
                    <option value="Multiple devices">Multiple devices</option>
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Available Time for Learning *
                  </label>
                  <select
                    name="timeAvailability"
                    value={formData.timeAvailability}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select time</option>
                    <option value="Less than 1 hour/week">Less than 1 hour/week</option>
                    <option value="1-3 hours/week">1-3 hours/week</option>
                    <option value="3-5 hours/week">3-5 hours/week</option>
                    <option value="More than 5 hours/week">More than 5 hours/week</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motivation Level *
                  </label>
                  <select
                    name="motivationLevel"
                    value={formData.motivationLevel}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  >
                    <option value="">Select level</option>
                    <option value="Extremely motivated">Extremely motivated</option>
                    <option value="Very motivated">Very motivated</option>
                    <option value="Moderately motivated">Moderately motivated</option>
                    <option value="Somewhat motivated">Somewhat motivated</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 py-4 rounded-lg text-white text-lg font-bold transition-colors disabled:opacity-50"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    <span>Completing Registration...</span>
                  </div>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    <span>Complete Registration</span>
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Registration;
